import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from '../utils/theme';
import { API_BASE_URL } from '../utils/ApiManager';
import i18n from '../i18n';

// Helper function to get localized value
const getLocalizedValue = (obj, lang = i18n.language) => {
  if (!obj) return '';
  if (typeof obj !== 'object') return obj;
  if (obj[lang]) return obj[lang];
  
  // Fallback to first available language
  const firstLang = Object.keys(obj)[0];
  return obj[firstLang] || '';
};

const UploadCard = ({ 
  upload, 
  imageThemes, 
  prompts, 
  suggestions = [],
  onImageThemeChange,
  onAddAction,
  onRemoveAction,
  onActionChange,
  onVarietyChange,
  onDelete 
}) => {
  const { t } = useTranslation('upload');
  const [showEnlarged, setShowEnlarged] = useState(false);

  // Helper function to get secure image URL (add this but keep everything else the same)
  const getSecureImageUrl = (preview) => {
    // Handle blob URLs (for newly selected files)
    if (preview?.startsWith('blob:')) return preview;
    
    // Handle secure URLs (from the new system)
    if (preview?.startsWith('/api/files/access')) {
      return `${API_BASE_URL}${preview}`;
    }
    
    // Handle absolute paths (starting with /uploads/) - legacy support during transition
    if (preview?.startsWith('/uploads/')) {
      return `${API_BASE_URL}${preview}`;
    }
    
    // Handle relative paths (just filename, without /uploads/) - legacy support
    if (preview && !preview.includes('/')) {
      return `${API_BASE_URL}/uploads/${preview}`;
    }

    // Default case: return the preview as is
    return preview;
  };
  
  const previewUrl = getSecureImageUrl(upload.preview);
  
  // Function to filter available prompts based on the selected theme and existing actions
  const getAvailablePrompts = (currentActionIndex) => {
    if (!upload.imageTheme) return [];
    
    // Get all prompts for this theme
    const themePrompts = prompts.filter(p => {
      const promptTheme = p.imageTheme;
      // If it's an object (multilingual), get the appropriate version
      let theme = promptTheme;
      if (typeof promptTheme === 'object' && promptTheme !== null) {
        theme = promptTheme.en || Object.values(promptTheme)[0];
      }
      return theme === upload.imageTheme;
    });
    
    // If we're not checking for an existing action index, return all prompts for the theme
    if (currentActionIndex < 0) return themePrompts;
    
    // Otherwise, include prompts that are either not used or used by this action
    const usedPromptIds = upload.actions
      .map((a, i) => i !== currentActionIndex ? a.promptId : null)
      .filter(id => id !== null);
    
    return themePrompts.filter(p => !usedPromptIds.includes(p.id));
  };

  return (
    <div className={`bg-white rounded-lg shadow-md relative flex-shrink-0 m-6 overflow-hidden h-auto w-60`}>
      {/* Background SVG with circles */}
      <svg
        className="absolute bottom-0 left-0 mb-8 scale-150 group-hover:scale-[1.65] transition-transform"
        viewBox="0 0 375 283"
        fill="none"
        style={{ opacity: 0.05, zIndex: -1 }}
      >
        <circle cx="205.52" cy="60" r="50" fill="currentColor" />
        <circle cx="76" cy="183.48" r="76" fill="currentColor" />
        <circle cx="280" cy="183.48" r="40" fill="currentColor" />
      </svg>

      {/* Image Container */}
      <div className="relative w-full h-48 cursor-zoom-in" onClick={() => setShowEnlarged(true)}>
        {previewUrl && (
          <img
            src={previewUrl}
            alt="upload preview"
            className="w-full h-full object-cover transition-transform duration-300 transform hover:scale-105"
          />
        )}
      </div>

      {/* Enlarged Image Modal */}
      {showEnlarged && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center"
          onClick={() => setShowEnlarged(false)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <button
              onClick={() => setShowEnlarged(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {previewUrl && (
              <img
                src={previewUrl}
                alt="enlarged preview"
                className="max-h-[90vh] max-w-[90vw] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        </div>
      )}

      {/* Actions Section */}
      <div className="p-4 bg-white">
        {/* Remove Image Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={onDelete}
            className={`${theme.button.base} ${theme.button.secondary} text-sm px-4 py-2 hover:bg-red-500 hover:text-white hover:border-red-500`}
          >
            {t('buttons.removeImage')}
          </button>
        </div>

        {/* Image Theme Selection */}
        <div className="mb-4">
          <label className="text-sm font-medium text-gray-700 block mb-2">
            {t('labels.photoSubject')}
          </label>
          <select
            value={upload.imageTheme || ''}
            onChange={(e) => onImageThemeChange(e.target.value)}
            className="bg-white border border-gray-200 rounded-lg text-sm w-full p-2 text-gray-700"
          >
            <option value="">{t('labels.selectTheme')}</option>
            {imageThemes.map((theme) => (
              <option 
                key={typeof theme === 'object' ? JSON.stringify(theme) : theme} 
                value={getLocalizedValue(theme)}
              >
                {getLocalizedValue(theme)}
              </option>
            ))}
          </select>
        </div>

        {/* Actions List */}
        {upload.actions && upload.actions.map((actionItem, actionIndex) => (
          <div key={actionIndex} className="bg-gray-50 rounded-lg mb-3 p-3">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-medium text-gray-700">
                {t('labels.animationEffect', { number: actionIndex + 1 })}
              </span>
              {upload.actions.length > 1 && (
                <button
                  onClick={() => onRemoveAction(actionIndex)}
                  className={`${theme.button.base} ${theme.button.secondary} text-xs p-2 rounded-full hover:bg-red-500 hover:text-white hover:border-red-500`}
                >
                  ×
                </button>
              )}
            </div>
            
            <select
              value={actionItem.promptId || ''}
              onChange={(e) => onActionChange(actionIndex, e.target.value)}
              className="bg-white border border-gray-200 rounded-lg text-xs mb-2 w-full p-2 text-gray-700"
            >
              <option value="">Select an action</option>
              {getAvailablePrompts(actionIndex).map((prompt) => (
                <option key={prompt.id} value={prompt.id}>
                  {getLocalizedValue(prompt.action)}
                </option>
              ))}
            </select>

            {/* Pictogram Display */}
            {actionItem.promptId && (() => {
              const selectedPrompt = prompts.find(p => p.id === actionItem.promptId);
              if (selectedPrompt?.suggestions?.length > 0) {
                const suggestion = suggestions.find(s => s.key === selectedPrompt.suggestions[0]);
                if (suggestion) {
                  return (
                    <div className="mb-2">
                      <div className="text-sm text-gray-600 mb-1 flex items-center">
                        <span>{t('labels.suggestions')}</span>
                        <span 
                          className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs font-medium text-gray-500 bg-gray-100 rounded-full cursor-help hover:bg-gray-200"
                          title={t('labels.suggestionsHint')}
                        >
                          ?
                        </span>
                      </div>
                      <div className="flex">
                        <img 
                          src={suggestion.icon} 
                          alt={getLocalizedValue(suggestion.name) || suggestion.key} 
                          className="h-4 w-4"
                        />
                      </div>
                    </div>
                  );
                }
              }
              return null;
            })()}

            <div className="flex items-center">
              <label className="text-sm text-gray-700 mr-2">
                {t('labels.numberOfVersions')}
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={actionItem.variety || 1}
                onChange={(e) => onVarietyChange(actionIndex, e.target.value)}
                className="bg-white border border-gray-200 rounded-lg w-16 text-xs p-2 text-gray-700"
              />
            </div>
          </div>
        ))}

        {/* Add Action Button - Only show if a theme is selected and there are available prompts */}
        {upload.imageTheme && (
          <button
            onClick={onAddAction}
            disabled={getAvailablePrompts(-1).length === 0}
            className={`${theme.button.base} ${theme.button.primary} w-full mt-2 p-2 text-xs
              ${getAvailablePrompts(-1).length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {t('buttons.addEffect')}
          </button>
        )}
      </div>
    </div>
  );
};

export default UploadCard;